// src/auth/AuthContext.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { http } from "./apiClient.ts";
import { tokenStore, Keys } from "./tokenStore.ts";
import { parseUserFromJwt } from "./jwt.ts";

/* ========= Types ========= */

export interface AuthUser {
  id: number;
  name: string | null;
  role: string | number | null;
  officeId: number;
}

export interface LoginParams {
  email: string;
  password: string;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  /** ISO string or epoch ms */
  expiresAt: string | number | Date;
  role: string | number;
  userId: number;
  officeId: number;
  username: string;
}

export interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (p: LoginParams) => Promise<boolean>;
  logout: () => Promise<void>;
}

/* ========= Helpers ========= */

const toEpochSeconds = (expiresAt: string | number | Date): number => {
  if (typeof expiresAt === "number") {
    // assume epoch ms or seconds (heuristic)
    return expiresAt > 10_000_000_000 ? Math.floor(expiresAt / 1000) : Math.floor(expiresAt);
  }
  return Math.floor(new Date(expiresAt).getTime() / 1000);
};

type MaybeJwt = {
  sub?: string | number;
  id?: string | number;
  role?: string | number;
  name?: string;
  username?: string;
  officeId?: string | number;
};

/* ========= Context ========= */

const defaultAuth: AuthContextValue = {
  user: null,
  isAuthenticated: false,
  loading: true,
  login: async () => false,
  logout: async () => {},
};

const AuthCtx = createContext<AuthContextValue>(defaultAuth);

export const useAuth = (): AuthContextValue => useContext(AuthCtx);

/* ========= Provider ========= */

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // восстановление пользователя из localStorage/JWT
  useEffect(() => {
    const access = tokenStore.get(Keys.Access);
    if (access) {
      const jwt = (parseUserFromJwt(access) || {}) as MaybeJwt;
      const id = Number(tokenStore.get(Keys.User) || jwt.sub || jwt.id || 0);
      const officeId = Number(tokenStore.get(Keys.Office) || jwt.officeId || 0);
      const role = (tokenStore.get(Keys.Role) || jwt.role || null) as string | number | null;
      const name = (tokenStore.get(Keys.Name) || jwt.name || jwt.username || null) as string | null;
      setUser({ id, name, role, officeId });
    } else {
      setUser(null);
    }
    setLoading(false);
  }, []);

  const login = async ({ email, password }: LoginParams): Promise<boolean> => {
    const { data } = await http.post<LoginResponse>("/api/auth/login", { email, password });

    tokenStore.set(Keys.Access, data.accessToken);
    tokenStore.set(Keys.Refresh, data.refreshToken);
    tokenStore.set(Keys.ExpAt, String(toEpochSeconds(data.expiresAt)));
    tokenStore.set(Keys.Role, String(data.role));
    tokenStore.set(Keys.User, String(data.userId));
    tokenStore.set(Keys.Office, String(data.officeId));
    tokenStore.set(Keys.Name, data.username);

    setUser({ id: data.userId, name: data.username, role: data.role, officeId: data.officeId });
    return true;
  };

  const logout = async (): Promise<void> => {
    tokenStore.clearAll();
    setUser(null);
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      loading,
      login,
      logout,
    }),
    [user, loading]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
